import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { Logo } from "@/components/landing/Navbar";
import { LanguageSwitcher } from "@/components/landing/LanguageSwitcher";
import { useLang } from "@/i18n/LanguageContext";
import { privacyContent } from "@/i18n/privacyContent";

const Paragraph = ({ text }) => {
  const parts = text.split(/(https?:\/\/[^\s]+)/g);
  return (
    <p className="text-slate-400 leading-relaxed">
      {parts.map((p, i) =>
        p.match(/^https?:\/\//) ? (
          <a
            key={i}
            href={p}
            target="_blank"
            rel="noopener noreferrer"
            className="text-emerald-400 underline underline-offset-2 break-all hover:text-emerald-300"
          >
            {p}
          </a>
        ) : (
          <span key={i}>{p}</span>
        )
      )}
    </p>
  );
};

export default function Confidentialite() {
  const { lang } = useLang();
  const c = privacyContent[lang] || privacyContent.en;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#080A09] font-dm-sans">
      <header className="sticky top-0 z-50 bg-[#080A09]/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" data-testid="privacy-logo">
            <Logo />
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link
              to="/"
              data-testid="privacy-back-link"
              className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-slate-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              {c.back}
            </Link>
          </div>
        </div>
      </header>

      <main className="relative">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 h-[400px] w-[400px] rounded-full bg-emerald-500/10 blur-3xl" />

        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 pt-20 pb-28">
          <div className="text-center" data-testid="privacy-header">
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-500/10 px-4 py-1.5 text-xs font-semibold text-emerald-300">
              <ShieldCheck className="h-4 w-4" />
              {c.badge}
            </span>
            <h1 className="mt-5 font-outfit text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
              {c.title}
            </h1>
            <p className="mt-4 text-lg text-slate-400">{c.subtitle}</p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3 text-xs font-semibold">
              <span className="rounded-full border border-emerald-400/30 bg-emerald-500/10 px-4 py-1.5 text-emerald-300">
                {c.version}
              </span>
              <span className="rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-slate-400">
                {c.updated}
              </span>
            </div>
          </div>

          <div className="mt-10 rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
            <p className="text-slate-300 leading-relaxed">{c.intro}</p>
          </div>

          <div className="mt-10 space-y-10">
            {c.sections.map((s) => (
              <section key={s.n} data-testid={`privacy-section-${s.n}`}>
                <div className="flex items-center gap-4">
                  <span
                    className={`grid place-items-center h-9 w-9 shrink-0 rounded-full font-outfit font-bold text-sm ${
                      s.highlight
                        ? "bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-400/40"
                        : "bg-emerald-400 text-emerald-950"
                    }`}
                  >
                    {s.n}
                  </span>
                  <h2 className="font-outfit text-xl sm:text-2xl font-bold text-white">
                    {s.title}
                  </h2>
                </div>

                <div
                  className={`mt-4 ml-0 sm:ml-[3.25rem] space-y-3 ${
                    s.highlight
                      ? "rounded-2xl border border-emerald-400/25 bg-emerald-500/[0.06] p-6"
                      : ""
                  }`}
                >
                  {s.body?.map((p, i) => (
                    <Paragraph key={i} text={p} />
                  ))}
                  {s.list && (
                    <ul className="space-y-2 pt-1">
                      {s.list.map((li, i) => (
                        <li key={i} className="flex gap-3 text-slate-400">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400" />
                          <span>{li}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {s.after?.map((p, i) => (
                    <Paragraph key={`a-${i}`} text={p} />
                  ))}
                </div>
              </section>
            ))}
          </div>

          <div className="mt-12 rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
            <p className="text-sm text-slate-400 leading-relaxed">{c.footerNote}</p>
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/"
              data-testid="privacy-footer-back"
              className="inline-flex items-center gap-2 rounded-full bg-emerald-400 px-7 py-3 text-sm font-bold text-emerald-950 hover:bg-emerald-300 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              {c.back}
            </Link>
            <p className="mt-8 text-sm text-slate-500">
              © {new Date().getFullYear()} Jos'FIT · Fait avec ❤️ en France
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
